import React from "react";
import { createPortal } from "react-dom";
import { FaUserGraduate } from "react-icons/fa";

const ViewModal = ({ consultant, onClose }) => {
  if (!consultant) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white shadow-2xl max-w-2xl w-full rounded-lg relative overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="
            absolute top-4 right-4 z-10
            w-8 h-8 flex items-center justify-center
            rounded-full bg-gray-100 text-gray-600
            hover:bg-gray-200 hover:text-gray-800
            focus:outline-none focus:ring-2 focus:ring-blue-500
            transition-all duration-200 shadow-sm"
        >
          ×
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-6">
          <h2 className="text-white text-xl font-semibold">
            Client Information
          </h2>
        </div>

        <div className="flex flex-col md:flex-row gap-8 p-8">
          {/* Image Container: show image if exists, else icon */}
          <div className="w-32 h-32 rounded-lg overflow-hidden border-4 border-white shadow-lg">
            {consultant.companyRegistrationProof ? (
              <img
                src={consultant.companyRegistrationProof}
                alt="Company Registration"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <FaUserGraduate className="w-16 h-16 text-blue-600" />
              </div>
            )}
          </div>

          {/* Info Container */}
          <div className="flex-1 space-y-4">
            <div className="space-y-4">
              {/* Client Name and Company Name - One Row */}
              <div className="flex gap-4">
                <div className="flex-1 border-l-4 border-blue-500 pl-4">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Client Name
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {consultant.consultantName}
                  </p>
                </div>

                <div className="flex-1 border-l-4 border-orange-500 pl-4">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Company Name
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {consultant.companyName}
                  </p>
                </div>
              </div>

              {/* Email and Phone - One Row */}
              <div className="flex gap-4">
                <div className="flex-1 border-l-4 border-purple-500 pl-4">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Email
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mt-1 break-all">
                    {consultant.email}
                  </p>
                </div>

                <div className="flex-1 border-l-4 border-green-500 pl-4">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Phone
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {consultant.phone}
                  </p>
                </div>
              </div>

              {/* Certificate and Aadhar Card - One Row */}
              {(consultant.certificate || consultant.aadharCard) && (
                <div className="flex gap-4">
                  <div className="flex-1">
                    {consultant.certificate ? (
                      <div className="border-l-4 border-indigo-500 pl-4">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                          Certificate
                        </p>
                        <p className="text-lg font-semibold text-gray-900 mt-1">
                          {consultant.certificate}
                        </p>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>

                  <div className="flex-1">
                    {consultant.aadharCard ? (
                      <div className="border-l-4 border-yellow-500 pl-4">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                          Aadhar Card
                        </p>
                        <p className="text-lg font-semibold text-gray-900 mt-1">
                          {consultant.aadharCard}
                        </p>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              )}

              {/* Country and Status - One Row */}
              {(consultant.country || consultant.status) && (
                <div className="flex gap-4">
                  <div className="flex-1">
                    {consultant.country ? (
                      <div className="border-l-4 border-red-500 pl-4">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                          Country
                        </p>
                        <p className="text-lg font-semibold text-gray-900 mt-1">
                          {consultant.country}
                        </p>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>

                  <div className="flex-1">
                    {consultant.status ? (
                      <div className="border-l-4 border-pink-500 pl-4">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                          Status
                        </p>
                        <p
                          className={`text-lg font-semibold mt-1 capitalize ${
                            consultant.status === "approved"
                              ? "text-green-600"
                              : consultant.status === "rejected"
                              ? "text-red-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {consultant.status}
                        </p>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              )}

              {/* Message - Full Width */}
              {consultant.message && (
                <div className="w-full border-l-4 border-teal-500 pl-4">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Message
                  </p>
                  <p className="text-base text-gray-900 mt-1 leading-relaxed">
                    {consultant.message}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewModal;
